import { auditFieldsForCreate, currentUserId, newId } from '../engine/context'
import { setSubmission, startStatus, WorkflowError } from '../engine/workflowEngine'
import { findRow, insertRow, readTable } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { toErrorResponse } from './workflow.handler'

/** Helper bersama handler C07 (transfer, retur WIP, retur tembakau) — pola `materialRequest.handler.ts`. */

export const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

export const ok = (data: unknown) => [200, { statusCode: 200, success: true, data }]

export function fail(message: string, code = 'ERR_VALIDATION_FAILED', status = 400): never {
  throw new WorkflowError(message, code, status)
}

export const pad = (value: number, width: number) => String(value).padStart(width, '0')
export const round = (value: number) => Math.round(value * 10000) / 10000
export const parse = <T>(data: unknown) => JSON.parse(String(data ?? '{}')) as T

export const handle = (run: () => unknown) => {
  try {
    return ok(run())
  } catch (error) {
    return toErrorResponse(error)
  }
}

export function roleCodesOf(userId: string): string[] {
  return readTable('mt_privileges')
    .filter(row => idOf(row.user) === userId)
    .map(row => String(findRow('mt_roles', idOf(row.role))?.code ?? ''))
}

export function requireRole(message: string, ...roles: string[]) {
  if (!roleCodesOf(currentUserId()).some(code => roles.includes(code))) fail(message, 'ERR_UNAUTHORIZED_ROLE', 403)
}

export function nextDocNo(prefix: string, collection: string, field: string, date = new Date()) {
  const day = `${String(date.getFullYear()).slice(2)}${pad(date.getMonth() + 1, 2)}${pad(date.getDate(), 2)}`
  const pattern = new RegExp(`^${prefix}-${day}-(\\d{3})`)
  const highest = readTable(collection)
    .map(row => pattern.exec(String(row[field] ?? '')))
    .reduce((max, match) => (match ? Math.max(max, Number(match[1])) : max), 0)
  return `${prefix}-${day}-${pad(highest + 1, 3)}`
}

/**
 * Gudang baris `tx_log` dari kode halaman (INV-02 kolom/filter "Gudang", footer INV-03) — konvensi seed `wms-inventory`:
 * halaman RM (pengadaan, inbound RM, request, retur, transfer, WIP, NG, pemusnahan RM, setup TAT, INV-01 NG) → WH-RM;
 * halaman FG → WH-FG; halaman lintas gudang (master, umum) → null. Opname memakai `txLogWh` (gudang sesi).
 */
const LOG_WAREHOUSE: [RegExp, string][] = [
  [/^(PRC|IRM|REQ|RTN|TRF|WIP|DSR|NG|TAT|INV-01)/, 'WH-RM'],
  [/^(IFG|OFG|GAB|KSL|CLM|DSP|RTS)/, 'WH-FG']
]

export function logWarehouseId(pageCode: string): string | null {
  const code = LOG_WAREHOUSE.find(([pattern]) => pattern.test(pageCode))?.[1]
  return code ? (readTable('mt_warehouses').find(row => row.code === code)?.id ?? null) : null
}

export function txLog(logType: string, pageCode: string, action: string, docRef: string, detail: string) {
  insertRow('tx_log', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: new Date().toISOString(),
    log_type: logType,
    page_code: pageCode,
    action,
    doc_ref: docRef,
    detail,
    warehouse_id: logWarehouseId(pageCode),
    user_id: currentUserId()
  })
}

export function stockMovement(
  type: string,
  unit: LocalRow,
  qtySmall: number,
  from: unknown,
  to: unknown,
  docRef: string
) {
  insertRow('stock_movement', {
    ...auditFieldsForCreate(),
    id: newId(),
    ts: new Date().toISOString(),
    movement_type: type,
    unit_id: unit.id,
    qty_small: qtySmall,
    from_rack_bin_id: from ?? null,
    to_rack_bin_id: to ?? null,
    from_warehouse_zone_id: null,
    to_warehouse_zone_id: null,
    doc_ref: docRef
  })
}

export const statusCodeOf = (row: LocalRow) => String(findRow('wml_status', idOf(row.status_id))?.code ?? '')

/** Pembuat `runAction` untuk satu workflow: cari workflow/aksi/actor lewat kode dan jalankan `setSubmission`. */
export function workflowRunner(workflowCode: string, entityPath: string) {
  const workflow = () => {
    const row = readTable('wml_workflow').find(item => item.code === workflowCode)
    if (!row) fail(`Workflow ${workflowCode} tidak ditemukan.`, 'WORKFLOW_NOT_FOUND', 404)
    return row
  }
  const actionOf = (name: string) => {
    const row = readTable('wml_action').find(item => item.name === name && idOf(item.workflow_id) === workflow().id)
    if (!row) fail(`Action ${name} tidak ditemukan.`, 'ACTION_NOT_FOUND', 404)
    return row
  }
  const actorFor = (...codes: string[]) => {
    const roles = readTable('mt_privileges')
      .filter(row => idOf(row.user) === currentUserId())
      .map(row => idOf(row.role))
    const actor = readTable('wml_actor').find(
      row =>
        codes.includes(String(row.code)) && idOf(row.workflow_id) === workflow().id && roles.includes(idOf(row.role_id))
    )
    if (!actor) fail('Actor tidak berhak menjalankan aksi ini.', 'ERR_UNAUTHORIZED_ROLE', 403)
    return actor
  }
  const run = (
    entity: LocalRow,
    name: string,
    actorCodes: string[],
    patch: Record<string, unknown> = {},
    reason = ''
  ) =>
    setSubmission({
      workflow_id: workflow().id,
      action_id: actionOf(name).id,
      submission_id: idOf(entity.submission_id) || null,
      entity_id: entity.id,
      actor_id: actorFor(...actorCodes).id,
      reason_text: reason,
      native_document_payload: { path: entityPath, payload: { id: entity.id, ...patch } }
    })
  /** Buat baris `wxl_submission` di status awal untuk entitas baru. */
  const start = (entityId: string) => {
    const status = startStatus(workflow().id)
    const submission = insertRow('wxl_submission', {
      ...auditFieldsForCreate(),
      id: newId(),
      workflow_id: workflow().id,
      entity: entityPath,
      entity_id: entityId,
      source: 'ACTION',
      current_status_id: status?.id ?? null,
      last_action_id: null,
      workflow_snapshot: null
    })
    return { statusId: status?.id ?? null, submissionId: submission.id as string }
  }
  return { run, start }
}

export const materialOf = (materialId: unknown) => findRow('mt_material', idOf(materialId))
export const bigUnitNameOf = (material: LocalRow | undefined) =>
  String(findRow('mt_unit', idOf(material?.default_purchase_unit_id))?.name ?? '')

/**
 * Setelah tugas putaway selesai: retur WIP / tembakau berstatus "Disetujui & Dicetak" yang seluruh unitnya sudah
 * tersimpan (IN_STOCK / NG_IN_QUARANTINE) otomatis ke "In Storage" (`ACT_WIP_STORE` / `ACT_TOB_RTN_FINISH`).
 */
export function closeReturnsAfterPutaway() {
  const stored = (unitId: unknown) => {
    const unit = findRow('rm_unit', idOf(unitId))
    return !unitId || unit?.unit_status === 'IN_STOCK' || unit?.unit_status === 'NG_IN_QUARANTINE'
  }
  const wip = workflowRunner('WF-WIP-01', 'wip_return')
  readTable('wip_return')
    .filter(row => statusCodeOf(row) === 'WIP_CHECKED')
    .forEach(row => {
      const lines = readTable('wip_return_line').filter(line => idOf(line.wip_return_id) === row.id)
      if (lines.every(line => stored(line.good_unit_id) && stored(line.ng_unit_id)))
        wip.run(row, 'ACT_WIP_STORE', ['ACT_WIP_OP_RM'])
    })
  const tob = workflowRunner('WF-TOB-02', 'rm_tobacco_return')
  readTable('rm_tobacco_return')
    .filter(row => statusCodeOf(row) === 'TOB_RTN_CHECKED')
    .forEach(row => {
      const lines = readTable('rm_tobacco_return_line').filter(line => idOf(line.return_id) === row.id)
      if (lines.length && lines.every(line => line.unit_id && stored(line.unit_id)))
        tob.run(row, 'ACT_TOB_RTN_FINISH', ['ACT_TOB_RTN_OP_RM'])
    })
  /*
   * Penerimaan Tembakau (WF-TOB-01): "Selesai" saat seluruh label setiap jenis sudah berdiri di rak (stok per label,
   * keputusan user 2026-10-21 #1). Label = `rm_unit` dengan batch baris penerimaan.
   */
  const receipt = workflowRunner('WF-TOB-01', 'rm_tobacco_receipt')
  readTable('rm_tobacco_receipt')
    .filter(row => statusCodeOf(row) === 'TOB_REC_APPROVED')
    .forEach(row => {
      const lines = readTable('rm_tobacco_receipt_line').filter(line => idOf(line.receipt_id) === row.id)
      if (!lines.length || lines.some(line => !line.batch_no)) return
      const labels = readTable('rm_unit').filter(unit => lines.some(line => line.batch_no === unit.batch_no))
      if (!labels.length || labels.some(unit => unit.unit_status !== 'IN_STOCK')) return
      try {
        receipt.run(row, 'ACT_TOB_REC_STORE', ['ACT_TOB_OP_RM'], {
          is_stored: true,
          stored_at: new Date().toISOString()
        })
      } catch {
        /* Penutupan dokumen tidak boleh menggagalkan penempatan label (aktor bukan operator, mis. uji / admin). */
      }
    })
}
